import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import {
  purchaseOrderFormSchema,
  type PurchaseOrderFormData,
} from '@/lib/validations/clientPurchaseOrder'
import { sanitizePurchaseOrderId } from '@/lib/validations/auth'
import {
  createPurchaseOrder,
  fetchPurchaseOrder,
  updatePurchaseOrder,
} from '@/lib/services/clientPurchaseOrderService'
import { fetchClientOptionsList } from '@/lib/services/clientContextService'
import { useToastNotification } from '@/lib/utils/useToastNotification'

interface PurchaseOrderFormProps {
  purchaseOrderId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

interface ClientOption {
  id: number
  code: string
  name: string
}

const emptyForm = (): PurchaseOrderFormData => ({
  client_id: 0,
  po_number: '',
  po_date: '',
  domain: '',
  description: '',
  portfolio: '',
  sales_person: '',
  amount: null,
  duration: '',
  start_date: '',
  end_date: '',
})

const normalizeDateValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  return String(value).slice(0, 10)
}

const formatDurationParts = (months: number, days: number): string => {
  const parts: string[] = []
  if (months > 0) parts.push(months === 1 ? '1 month' : `${months} months`)
  if (days > 0) parts.push(days === 1 ? '1 day' : `${days} days`)
  return parts.join(' ')
}

const computeDurationFromDates = (startDate: string, endDate: string): string => {
  const start = normalizeDateValue(startDate)
  const end = normalizeDateValue(endDate)
  if (!start || !end) return ''

  const startDt = new Date(`${start}T00:00:00`)
  const endDt = new Date(`${end}T00:00:00`)
  if (Number.isNaN(startDt.getTime()) || Number.isNaN(endDt.getTime()) || endDt < startDt) return ''

  let months = 0
  let cursor = new Date(startDt)
  while (true) {
    const nextMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, cursor.getDate())
    if (nextMonth > endDt) break
    months += 1
    cursor = nextMonth
  }

  const msPerDay = 24 * 60 * 60 * 1000
  const days = Math.floor((endDt.getTime() - cursor.getTime()) / msPerDay) + 1
  return formatDurationParts(months, days)
}

export function PurchaseOrderForm({ purchaseOrderId, onSuccess, onCancel }: PurchaseOrderFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([])
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!purchaseOrderId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<PurchaseOrderFormData>({
    resolver: zodResolver(purchaseOrderFormSchema),
    defaultValues: emptyForm(),
  })

  const startDate = watch('start_date')
  const endDate = watch('end_date')

  useEffect(() => {
    fetchClientOptionsList()
      .then((clients) => {
        setClientOptions(
          clients.map((c) => ({
            id: c.id,
            code: c.code,
            name: c.name,
          }))
        )
      })
      .catch(() => setClientOptions([]))
  }, [])

  useEffect(() => {
    const duration = computeDurationFromDates(startDate ?? '', endDate ?? '')
    setValue('duration', duration, { shouldValidate: false })
  }, [startDate, endDate, setValue])

  useEffect(() => {
    if (!isEditMode || !purchaseOrderId) return

    const load = async () => {
      setIsLoading(true)
      try {
        const po = await fetchPurchaseOrder(purchaseOrderId)
        reset({
          id: sanitizePurchaseOrderId(po.id),
          client_id: po.client_id,
          po_number: po.po_number ?? '',
          po_date: normalizeDateValue(po.po_date),
          domain: po.domain ?? '',
          description: po.description ?? '',
          portfolio: po.portfolio ?? '',
          sales_person: po.sales_person ?? '',
          amount: po.amount ?? null,
          duration: po.duration ?? computeDurationFromDates(po.start_date ?? '', po.end_date ?? ''),
          start_date: normalizeDateValue(po.start_date),
          end_date: normalizeDateValue(po.end_date),
        })
      } catch (e) {
        showError(e)
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [isEditMode, purchaseOrderId, reset, showError])

  const onSubmit = async (data: PurchaseOrderFormData) => {
    setIsSubmitting(true)
    try {
      const payload: PurchaseOrderFormData = {
        ...data,
        duration: computeDurationFromDates(data.start_date ?? '', data.end_date ?? '') || data.duration || null,
      }

      if (isEditMode && purchaseOrderId) {
        await updatePurchaseOrder(purchaseOrderId, payload)
        showSuccess('Success', 'Purchase order updated successfully')
      } else {
        await createPurchaseOrder(payload)
        showSuccess('Success', 'Purchase order created successfully')
      }
      onSuccess?.()
    } catch (e) {
      showError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box p={4}>
        <Text>Loading purchase order...</Text>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <input type="hidden" {...register('id')} />

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Client <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.client_id} isRequired>
            <Select
              placeholder="Select client"
              {...register('client_id', { valueAsNumber: true })}
              isDisabled={isEditMode}
            >
              {clientOptions.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name} ({client.code})
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.client_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            PO No. <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.po_number} isRequired>
            <Input {...register('po_number')} placeholder="CPO-2026-001" />
            <FormErrorMessage>{errors.po_number?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            PO Date <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.po_date} isRequired>
            <Input type="date" {...register('po_date')} />
            <FormErrorMessage>{errors.po_date?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Service Area</FormLabel>
          <FormControl isInvalid={!!errors.domain}>
            <Input {...register('domain')} placeholder="e.g., IT Infrastructure" />
            <FormHelperText>Optional contract or business area label (not an email domain).</FormHelperText>
            <FormErrorMessage>{errors.domain?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Portfolio</FormLabel>
          <FormControl isInvalid={!!errors.portfolio}>
            <Input {...register('portfolio')} placeholder="e.g., IT Resource Outsourcing" />
            <FormErrorMessage>{errors.portfolio?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Amount</FormLabel>
          <FormControl isInvalid={!!errors.amount}>
            <Input
              type="number"
              step="0.01"
              {...register('amount', {
                setValueAs: (value) =>
                  value === '' || value === null || value === undefined ? null : Number(value),
              })}
            />
            <FormErrorMessage>{errors.amount?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl isInvalid={!!errors.description}>
            <Textarea {...register('description')} rows={3} placeholder="PO description" />
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Sales Person</FormLabel>
          <FormControl isInvalid={!!errors.sales_person}>
            <Input {...register('sales_person')} placeholder="Sales person name" />
            <FormErrorMessage>{errors.sales_person?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Start Date <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.start_date} isRequired>
            <Input type="date" {...register('start_date')} />
            <FormErrorMessage>{errors.start_date?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            End Date <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.end_date} isRequired>
            <Input type="date" {...register('end_date')} />
            <FormErrorMessage>{errors.end_date?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Duration</FormLabel>
          <FormControl isInvalid={!!errors.duration}>
            <Input {...register('duration')} isReadOnly placeholder="Auto-calculated from dates" />
            <FormHelperText>Automatically calculated from start and end dates</FormHelperText>
            <FormErrorMessage>{errors.duration?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create Purchase Order'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
